import { commonsApiClient } from "./client";
import {
  FeatureRequest,
  FeatureRequestSubmission,
  FeatureRequestVoteResponse,
  FeatureRequestSubmissionResponse,
} from "../types/featureRequest";

/**
 * Feature Request API service for managing feature request operations
 * Uses CommonsApiClient for consistent API communication patterns
 */
export const featureRequestsApi = {
  /**
   * Fetches all feature requests
   * @returns Promise resolving to feature requests object
   */
  async getFeatureRequests(): Promise<Record<string, FeatureRequest>> {
    return await commonsApiClient.get<Record<string, FeatureRequest>>(
      "/api/feature-requests"
    );
  },

  /**
   * Submits a new feature request
   * @param submission - The feature request data to submit
   * @param userId - The ID of the user submitting the request
   * @returns Promise resolving to submission response
   */
  async submitFeatureRequest(
    submission: FeatureRequestSubmission,
    userId: string
  ): Promise<FeatureRequestSubmissionResponse> {
    return await commonsApiClient.post<FeatureRequestSubmissionResponse>(
      `/api/feature-requests/submission/${userId}`,
      {
        ...submission,
        userId,
      }
    );
  },

  /**
   * Votes for a feature request
   * @param userId - The ID of the user voting for the feature
   * @param featureId - The ID of the feature to vote for
   * @returns Promise resolving to vote response
   */
  async voteForFeature(
    userId: string,
    featureId: string
  ): Promise<FeatureRequestVoteResponse> {
    return await commonsApiClient.post<FeatureRequestVoteResponse>(
      `/api/feature-requests/vote/${userId}?featureId=${featureId}`,
      {}
    );
  },

  /**
   * Removes a vote from a feature request
   * @param userId - The ID of the user removing their vote
   * @param featureId - The ID of the feature to unvote
   * @returns Promise resolving to vote response
   */
  async unvoteFeature(
    userId: string,
    featureId: string
  ): Promise<FeatureRequestVoteResponse> {
    return await commonsApiClient.delete<FeatureRequestVoteResponse>(
      `/api/feature-requests/vote/${userId}?featureId=${featureId}`
    );
  },

  /**
   * Fetches user's voting status for all features
   * @param userId - The ID of the user
   * @returns Promise resolving to array of feature IDs the user has voted for
   */
  async getUserVotes(userId: string): Promise<string[]> {
    return await commonsApiClient.get<string[]>(
      `/api/feature-requests/votes/${userId}`
    );
  },
};
